<template>
  <dashboard-layout title="Cities" subtitle="The city pool the Astrocartography report draws its five places from">
    <template #actions>
      <router-link to="/astrocartography" class="link-btn">← Reports</router-link>
      <search-box v-model="search" placeholder="Search city / country…" />
      <button class="refresh-btn" @click="load" :disabled="loading">{{ loading ? 'Loading…' : 'Refresh' }}</button>
    </template>

    <div v-if="error" class="error-banner">⚠ {{ error }}</div>

    <loading-state v-if="loading && !cities.length" label="Loading city pool…" />

    <template v-else>
      <!-- Overview -->
      <stat-grid>
        <stat-card label="Cities" :value="cities.length.toLocaleString('en-US')" icon="globe" accent>
          <template #sub>{{ countOf('active') }} active · {{ countOf('paused') }} paused<template v-if="countOf('draft')"> · {{ countOf('draft') }} draft</template></template>
        </stat-card>
        <stat-card label="Countries" :value="countryCount.toLocaleString('en-US')" icon="share">
          <template #sub>{{ activeCountryCount }} with an active city</template>
        </stat-card>
        <stat-card label="Attribution needed" :value="attribCount.toLocaleString('en-US')" icon="filetext">
          <template #sub>{{ flaggedCount }} licence-flagged · {{ noPhotoCount }} no photo</template>
        </stat-card>
        <stat-card label="Ever picked" :value="pickedDistinct.toLocaleString('en-US')" icon="smile">
          <template #sub>{{ neverPickedActive }} active never picked · {{ generationCount }} runs</template>
        </stat-card>
      </stat-grid>

      <!-- Paused cities: editorial pause, flip-able -->
      <data-panel v-if="pausedCities.length" title="Paused">
        <div class="paused-list">
          <span v-for="c in pausedCities" :key="c.id" class="paused-chip" :title="c.pausedReason || 'paused'">
            {{ c.flag }} {{ c.name }}<span v-if="c.pausedReason" class="paused-reason"> · {{ truncate(c.pausedReason, 48) }}</span>
          </span>
        </div>
      </data-panel>

      <!-- Map of the pool + distribution by country -->
      <div class="map-grid">
        <data-panel title="Pool map">
          <world-map :dots="mapDots" />
          <div class="map-legend">
            <span><i class="lg-dot lg-active"></i> active</span>
            <span><i class="lg-dot lg-warn"></i> paused</span>
            <span v-if="countOf('draft')"><i class="lg-dot lg-draft"></i> draft</span>
            <span class="muted">· shows the current filter ({{ filteredCities.length }})</span>
          </div>
        </data-panel>
        <data-panel title="By country">
          <div class="dist-bars country-bars">
            <div
              v-for="s in countryDist"
              :key="s.code"
              class="dist-row clickable"
              :class="{ active: countryFilter === s.code }"
              @click="countryFilter = countryFilter === s.code ? 'all' : s.code"
            >
              <span class="dist-label" :title="s.label">{{ s.flag }} {{ s.label }}</span>
              <div class="dist-track"><div class="dist-fill" :style="{ width: s.pct + '%' }"></div></div>
              <span class="dist-val">{{ s.count }}</span>
            </div>
          </div>
        </data-panel>
      </div>

      <!-- Filters -->
      <div class="filter-rows">
        <div class="filter-line"><span class="fl-label">Status</span><filter-chips v-model="statusFilter" :options="statusOptions" /></div>
        <div class="filter-line">
          <span class="fl-label">Country</span>
          <select v-model="countryFilter" class="fig-select">
            <option value="all">All countries ({{ countryCount }})</option>
            <option v-for="c in countryDist" :key="c.code" :value="c.code">{{ c.flag }} {{ c.label }} ({{ c.count }})</option>
          </select>
        </div>
        <div class="filter-line"><span class="fl-label">View</span><filter-chips v-model="viewFilter" :options="viewOptions" /></div>
        <div class="filter-line"><span class="fl-label">Sort</span><filter-chips v-model="sortBy" :options="sortOptions" /></div>
      </div>

      <!-- Cities table -->
      <data-panel flush>
        <template #title>Cities <span class="muted">({{ filteredCities.length.toLocaleString('en-US') }})</span></template>
        <data-table :columns="cityColumns" :rows="filteredCities" row-key="id">
          <template #cell-photo="{ row }">
            <div class="city-photo">
              <img v-if="row.photo && row.photo.url" :src="row.photo.url" :alt="row.name" loading="lazy" referrerpolicy="no-referrer" :title="photoCredit(row.photo)" @error="onImgError">
              <div v-else class="city-nophoto">{{ row.flag || (row.name || '?').slice(0, 1) }}</div>
            </div>
          </template>
          <template #cell-name="{ row }">
            <div class="city-name">{{ row.name || '—' }}</div>
            <div class="city-desc" :title="row.description">{{ truncate(row.description, 44) }}</div>
          </template>
          <template #cell-country="{ row }">
            <span class="nowrap">{{ row.flag }} {{ row.country || '—' }}</span>
            <div class="muted small">{{ row.countryCode }}</div>
          </template>
          <template #cell-population="{ row }">
            <span class="num">{{ row.population ? formatPop(row.population) : '—' }}</span>
          </template>
          <template #cell-coords="{ row }"><span class="coords">{{ fmtCoord(row.latitude, row.longitude) }}</span></template>
          <template #cell-licence="{ row }">
            <span :title="row.photo ? [row.photo.license, row.photo.artist].filter(Boolean).join(' · ') : 'no photo'">
              <status-badge v-if="row.photo" :variant="licenceInfo(row).variant" :label="licenceInfo(row).short" />
              <span v-else class="muted">no photo</span>
            </span>
            <div v-if="row.photo && row.photo.artist" class="artist" :title="row.photo.artist">{{ truncate(row.photo.artist, 22) }}</div>
          </template>
          <template #cell-status="{ row }">
            <span :title="row.pausedReason">
              <status-badge :variant="statusVariant(row.status)" :label="row.status || '—'" />
            </span>
            <div v-if="row.status === 'paused' && row.pausedReason" class="paused-inline" :title="row.pausedReason">{{ truncate(row.pausedReason, 26) }}</div>
          </template>
          <template #cell-picked="{ row }">
            <span :class="pickedCount(row.id) ? 'strong' : 'never'">{{ pickedCount(row.id) }}</span>
          </template>
          <template #empty>No cities match your filters.</template>
        </data-table>
      </data-panel>
    </template>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { FirebaseAstrocartographyReportService } from '../services/firebaseAstrocartographyReportService'
import { classifyLicence } from '../services/licence'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import StatusBadge from './ui/StatusBadge.vue'
import WorldMap from './ui/WorldMap.vue'

const cityColumns = [
  { key: 'photo', label: '', width: '56px' },
  { key: 'name', label: 'City' },
  { key: 'country', label: 'Country', width: '160px' },
  { key: 'population', label: 'Population', width: '100px' },
  { key: 'coords', label: 'Coords', width: '130px' },
  { key: 'licence', label: 'Licence', width: '150px' },
  { key: 'status', label: 'Status', width: '150px' },
  { key: 'picked', label: 'Picked', width: '70px' }
]

export default {
  name: 'CityPoolDashboard',
  components: { DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, FilterChips, LoadingState, StatusBadge, WorldMap },
  setup() {
    const service = new FirebaseAstrocartographyReportService()

    const cities = ref([])
    const pickTally = ref({})
    const generationCount = ref(0)
    const loading = ref(true)
    const error = ref('')
    const search = ref('')
    const statusFilter = ref('all')
    const countryFilter = ref('all')
    const viewFilter = ref('all')
    const sortBy = ref('name')

    const load = async () => {
      loading.value = true
      error.value = ''
      try {
        const [c, gens] = await Promise.all([service.getCities(), service.getGenerations()])
        cities.value = c
        // How often each city was one of the five places, across all runs.
        const tally = {}
        gens.forEach(g => (g.places || []).forEach(p => { if (p.id) tally[p.id] = (tally[p.id] || 0) + 1 }))
        pickTally.value = tally
        generationCount.value = gens.length
      } catch (e) {
        console.error(e)
        const code = e && e.code ? e.code : ''
        if (code === 'permission-denied') error.value = 'Firestore denied the read (not signed in). Reload and try again.'
        else error.value = (e && e.message) || 'Failed to load cities.'
      } finally {
        loading.value = false
      }
    }

    const pickedCount = (id) => pickTally.value[id] || 0
    const licenceInfo = (row) => classifyLicence(row.photo ? row.photo.license : '')

    const countOf = (status) => cities.value.filter(c => c.status === status).length
    const pausedCities = computed(() => cities.value.filter(c => c.status === 'paused'))
    const countryCount = computed(() => new Set(cities.value.map(c => c.countryCode || c.country).filter(Boolean)).size)
    const activeCountryCount = computed(() => new Set(cities.value.filter(c => c.status === 'active').map(c => c.countryCode || c.country).filter(Boolean)).size)
    const attribCount = computed(() => cities.value.filter(c => c.photo && classifyLicence(c.photo.license).attrib).length)
    const flaggedCount = computed(() => cities.value.filter(c => c.photo && classifyLicence(c.photo.license).flag).length)
    const noPhotoCount = computed(() => cities.value.filter(c => !(c.photo && c.photo.url)).length)
    const pickedDistinct = computed(() => Object.keys(pickTally.value).filter(id => pickTally.value[id] > 0).length)
    const neverPickedActive = computed(() => cities.value.filter(c => c.status === 'active' && !pickTally.value[c.id]).length)

    // Countries, most cities first (clickable to filter).
    const countryDist = computed(() => {
      const map = {}
      cities.value.forEach(c => {
        const code = c.countryCode || c.country || '—'
        if (!map[code]) map[code] = { code, label: c.country || code, flag: c.flag || '', count: 0 }
        map[code].count++
      })
      const rows = Object.values(map).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
      const max = Math.max(...rows.map(r => r.count), 1)
      return rows.map(r => ({ ...r, pct: Math.round((r.count / max) * 100) }))
    })

    const statusOptions = computed(() => {
      const opts = [
        { value: 'all', label: 'All', count: cities.value.length },
        { value: 'active', label: 'Active', count: countOf('active') },
        { value: 'paused', label: 'Paused', count: countOf('paused') }
      ]
      if (countOf('draft')) opts.push({ value: 'draft', label: 'Draft', count: countOf('draft') })
      return opts
    })
    const viewOptions = computed(() => ([
      { value: 'all', label: 'All', count: cities.value.length },
      { value: 'never', label: 'Never picked', count: cities.value.filter(c => !pickTally.value[c.id]).length },
      { value: 'flagged', label: 'Licence flagged', count: flaggedCount.value },
      { value: 'nophoto', label: 'No photo', count: noPhotoCount.value }
    ]))
    const sortOptions = [
      { value: 'name', label: 'Name' },
      { value: 'picked', label: 'Most picked' },
      { value: 'population', label: 'Population' },
      { value: 'country', label: 'Country' }
    ]

    const filteredCities = computed(() => {
      const q = search.value.toLowerCase().trim()
      let rows = cities.value.filter(c => {
        if (statusFilter.value !== 'all' && c.status !== statusFilter.value) return false
        if (countryFilter.value !== 'all' && (c.countryCode || c.country || '—') !== countryFilter.value) return false
        if (viewFilter.value === 'never' && pickTally.value[c.id]) return false
        if (viewFilter.value === 'flagged' && !(c.photo && classifyLicence(c.photo.license).flag)) return false
        if (viewFilter.value === 'nophoto' && c.photo && c.photo.url) return false
        if (q) {
          const hay = `${c.name} ${c.label} ${c.country} ${c.countryCode} ${c.description} ${c.id}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
      rows = rows.slice()
      if (sortBy.value === 'picked') rows.sort((a, b) => pickedCount(b.id) - pickedCount(a.id) || a.name.localeCompare(b.name))
      else if (sortBy.value === 'population') rows.sort((a, b) => b.population - a.population || a.name.localeCompare(b.name))
      else if (sortBy.value === 'country') rows.sort((a, b) => a.country.localeCompare(b.country) || a.name.localeCompare(b.name))
      else rows.sort((a, b) => a.name.localeCompare(b.name))
      return rows
    })

    // The map follows the table's filter; colour by status.
    const mapDots = computed(() =>
      filteredCities.value
        .filter(c => c.latitude != null && c.longitude != null)
        .map(c => ({
          lat: c.latitude,
          lon: c.longitude,
          title: `${c.flag} ${c.name}, ${c.country} · ${c.status}${pickedCount(c.id) ? ' · picked ' + pickedCount(c.id) + '×' : ''}`,
          variant: c.status === 'active' ? 'active' : (c.status === 'paused' ? 'warn' : 'draft'),
          r: 1.3
        }))
    )

    const statusVariant = (s) => ({ active: 'success', paused: 'danger', draft: 'warning' }[s] || 'neutral')
    const truncate = (t, max) => !t ? '' : (t.length > max ? t.slice(0, max) + '…' : t)
    const formatPop = (n) => n >= 1e6 ? (n / 1e6).toFixed(n >= 1e7 ? 0 : 1) + 'M' : (n >= 1e3 ? Math.round(n / 1e3) + 'K' : String(n))
    const fmtCoord = (lat, lon) => (lat == null || lon == null) ? '—' : `${Math.abs(lat).toFixed(1)}°${lat >= 0 ? 'N' : 'S'} ${Math.abs(lon).toFixed(1)}°${lon >= 0 ? 'E' : 'W'}`
    const photoCredit = (ph) => ph ? [ph.artist, ph.license].filter(Boolean).join(' · ') : ''
    const onImgError = (e) => { const el = e.target; el.style.display = 'none'; if (el.parentElement) el.parentElement.classList.add('img-failed') }

    onMounted(load)

    return {
      cities, loading, error, search, generationCount,
      statusFilter, countryFilter, viewFilter, sortBy, statusOptions, viewOptions, sortOptions,
      cityColumns, pausedCities, countryCount, activeCountryCount, attribCount, flaggedCount, noPhotoCount,
      pickedDistinct, neverPickedActive, countryDist, filteredCities, mapDots,
      load, countOf, pickedCount, licenceInfo, statusVariant, truncate, formatPop, fmtCoord, photoCredit, onImgError
    }
  }
}
</script>

<style scoped>
.error-banner { background: rgba(248, 191, 113, 0.12); border: 1px solid rgba(248, 191, 113, 0.35); color: var(--warning); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.refresh-btn { padding: 9px 14px; background: var(--field-bg); border: 1px solid var(--panel-border); border-radius: var(--radius); color: var(--text-primary); font-size: 13px; cursor: pointer; }
.refresh-btn:hover { background: var(--hover-bg); }
.refresh-btn:disabled { opacity: 0.5; cursor: default; }
.link-btn { padding: 9px 14px; background: var(--field-bg); border: 1px solid var(--panel-border); border-radius: var(--radius); color: var(--primary-color); font-size: 13px; text-decoration: none; white-space: nowrap; cursor: pointer; }
.link-btn:hover { background: var(--hover-bg); }

.map-grid { display: grid; grid-template-columns: minmax(0, 2fr) minmax(260px, 1fr); gap: 16px; }
@media (max-width: 1000px) { .map-grid { grid-template-columns: 1fr; } }
.map-legend { display: flex; gap: 14px; flex-wrap: wrap; margin-top: 10px; font-size: 11.5px; color: var(--text-secondary); }
.lg-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 4px; vertical-align: middle; }
.lg-active { background: rgba(74, 222, 128, 0.85); }
.lg-warn { background: rgba(255, 107, 107, 0.9); }
.lg-draft { background: rgba(248, 191, 113, 0.85); }

.paused-list { display: flex; flex-wrap: wrap; gap: 8px; }
.paused-chip { font-size: 12.5px; padding: 4px 10px; border-radius: 12px; background: rgba(255, 107, 107, 0.1); border: 1px solid rgba(255, 107, 107, 0.3); color: var(--text-primary); }
.paused-reason { color: var(--text-secondary); font-size: 11.5px; }

.dist-bars { display: flex; flex-direction: column; gap: 6px; }
.country-bars { max-height: 330px; overflow-y: auto; padding-right: 4px; }
.dist-row { display: flex; align-items: center; gap: 10px; border-radius: 6px; padding: 1px 4px; }
.dist-row.clickable { cursor: pointer; }
.dist-row.clickable:hover { background: var(--hover-bg); }
.dist-row.active { background: rgba(108, 99, 255, 0.16); }
.dist-label { width: 140px; flex-shrink: 0; font-size: 12px; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dist-track { flex: 1; height: 8px; background: rgba(255, 255, 255, 0.06); border-radius: 4px; overflow: hidden; }
.dist-fill { height: 100%; border-radius: 4px; background: linear-gradient(90deg, var(--primary-color), var(--gradient-2)); }
.dist-val { width: 30px; text-align: right; font-size: 12px; color: var(--text-secondary); font-variant-numeric: tabular-nums; }

.filter-rows { display: flex; flex-direction: column; gap: 8px; }
.filter-line { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.fl-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); width: 58px; flex-shrink: 0; }
.fig-select { padding: 7px 10px; background: var(--field-bg); border: 1px solid var(--panel-border); border-radius: 8px; color: var(--text-primary); font-size: 12.5px; cursor: pointer; max-width: 260px; }

.muted { color: var(--text-secondary); font-weight: 400; }
.small { font-size: 11px; }
.strong { font-weight: 600; }
.never { color: var(--text-secondary); opacity: 0.5; }
.nowrap { white-space: nowrap; }
.num { font-variant-numeric: tabular-nums; }
.coords { font-size: 12px; color: var(--text-secondary); white-space: nowrap; font-variant-numeric: tabular-nums; }
.artist { font-size: 10.5px; color: var(--text-secondary); margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 140px; }
.paused-inline { font-size: 10.5px; color: var(--text-secondary); margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 140px; }

.city-photo { width: 44px; height: 44px; border-radius: 8px; overflow: hidden; background: var(--panel-bg); border: 1px solid var(--panel-border); }
.city-photo img { width: 100%; height: 100%; object-fit: cover; }
.city-nophoto { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 18px; color: var(--text-secondary); }
.city-name { font-weight: 600; font-size: 13px; color: var(--text-primary); }
.city-desc { font-size: 11px; color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 260px; }
</style>
